import { useState, useEffect, useLayoutEffect, useRef } from 'react';

import { Icon } from '@iconify/react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Labels are i18n keys, resolved with t() where they are displayed.
const TOOL_CONFIG = {
  get_canvas_document_content: {
    label: 'ai.thinkingTimeline.tools.getCanvasDocumentContent',
    icon: 'mdi:file-eye-outline',
  },
  suggest_word_changes: {
    label: 'ai.thinkingTimeline.tools.suggestWordChanges',
    icon: 'mdi:file-document-edit-outline',
  },
  modify_document: {
    label: 'ai.thinkingTimeline.tools.modifyDocument',
    icon: 'mdi:file-document-edit-outline',
  },
  edit_report_source: {
    label: 'ai.thinkingTimeline.tools.editReportSource',
    icon: 'mdi:file-document-check-outline',
  },
  create_folder: {
    label: 'ai.thinkingTimeline.tools.createFolder',
    icon: 'mdi:folder-plus-outline',
  },
  move_files: {
    label: 'ai.thinkingTimeline.tools.moveFiles',
    icon: 'mdi:file-move-outline',
  },
  rename_file: {
    label: 'ai.thinkingTimeline.tools.renameFile',
    icon: 'mdi:form-textbox',
  },
  delete_files: {
    label: 'ai.thinkingTimeline.tools.deleteFiles',
    icon: 'mdi:trash-can-outline',
  },
  search_web: {
    label: 'ai.thinkingTimeline.tools.searchWeb',
    icon: 'mdi:magnify',
  },
  search_documents: {
    label: 'ai.thinkingTimeline.tools.searchDocuments',
    icon: 'mdi:file-search-outline',
  },
  fetch_web_page: {
    label: 'ai.thinkingTimeline.tools.fetchWebPage',
    icon: 'mdi:link-variant',
  },
  execute_code: {
    label: 'ai.thinkingTimeline.tools.executeCode',
    icon: 'mdi:code-braces',
  },
  get_file: {
    label: 'ai.thinkingTimeline.tools.getFile',
    icon: 'mdi:file-download-outline',
  },
  browse_web: {
    label: 'ai.thinkingTimeline.tools.browseWeb',
    icon: 'mdi:web',
  },
  save_memory: {
    label: 'ai.thinkingTimeline.tools.saveMemory',
    icon: 'mdi:brain',
  },
  save_to_knowledge_base: {
    label: 'ai.thinkingTimeline.tools.saveToKnowledgeBase',
    icon: 'mdi:brain',
  },
  read_knowledge_base_page: {
    label: 'ai.thinkingTimeline.tools.readKnowledgeBasePage',
    icon: 'mdi:book-open-page-variant-outline',
  },
  search_knowledge_base: {
    label: 'ai.thinkingTimeline.tools.searchKnowledgeBase',
    icon: 'mdi:book-search-outline',
  },
  list_knowledge_base: {
    label: 'ai.thinkingTimeline.tools.listKnowledgeBase',
    icon: 'mdi:bookshelf',
  },
  delete_knowledge_base_page: {
    label: 'ai.thinkingTimeline.tools.deleteKnowledgeBasePage',
    icon: 'mdi:brain-off-outline',
  },
  record_learning: {
    label: 'ai.thinkingTimeline.tools.recordLearning',
    icon: 'mdi:school-outline',
  },
  record_error: {
    label: 'ai.thinkingTimeline.tools.recordError',
    icon: 'mdi:alert-circle-outline',
  },
  create_skill: {
    label: 'ai.thinkingTimeline.tools.createSkill',
    icon: 'mdi:puzzle-outline',
  },
  propose_soul_change: {
    label: 'ai.thinkingTimeline.tools.proposeSoulChange',
    icon: 'mdi:account-edit-outline',
  },
  manage_scheduled_task: {
    label: 'ai.thinkingTimeline.tools.manageScheduledTask',
    icon: 'mdi:calendar-clock',
  },
  run_skill: {
    label: 'ai.thinkingTimeline.tools.runSkill',
    icon: 'mdi:play-circle-outline',
  },
  list_skills: {
    label: 'ai.thinkingTimeline.tools.listSkills',
    icon: 'mdi:book-open-outline',
  },
  load_skill: {
    label: 'ai.thinkingTimeline.tools.loadSkill',
    icon: 'mdi:book-open-page-variant-outline',
  },
  read_skill_resource: {
    label: 'ai.thinkingTimeline.tools.readSkillResource',
    icon: 'mdi:file-document-outline',
  },
  analyzing: {
    label: 'ai.thinkingTimeline.tools.analyzing',
    icon: 'mdi:brain',
  },
  writing: {
    label: 'ai.thinkingTimeline.tools.writing',
    icon: 'mdi:pencil-outline',
  },
};

const DEFAULT_TOOL = {
  label: 'ai.thinkingTimeline.tools.default',
  icon: 'mdi:cog-outline',
};

/**
 * Timeline showing AI tool steps in a collapsible summary.
 *
 * Inspired by Qori's ThinkingTimeline, adapted for Neovision's tool-only steps.
 *
 * @param {Object} props
 * @param {Array}   props.steps        - Array of {tool, query?, status: 'active'|'completed'}
 * @param {boolean} props.isStreaming  - Whether the agent is still running
 * @param {boolean} props.isCollapsible - Whether to collapse after completion
 */
export function ThinkingTimeline({ steps, isStreaming = false, isCollapsible = false }) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(!isCollapsible);

  // Auto-collapse when streaming finishes
  useEffect(() => {
    if (!isStreaming && isCollapsible) {
      setIsExpanded(false);
    }
  }, [isStreaming, isCollapsible]);

  if (!steps || steps.length === 0) return null;

  const toolCount = steps.length;
  const activeStep = steps.find((step) => step.status === 'active') || steps[steps.length - 1];
  const activeConfig = TOOL_CONFIG[activeStep?.tool] || DEFAULT_TOOL;
  const summaryLabel = isStreaming ? t(activeConfig.label) : t('ai.thinkingTimeline.processed');

  return (
    <div className="mb-2">
      {/* Collapsed summary bar */}
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        className="flex items-center gap-1.5 py-1 px-2 -ml-2 rounded-md hover:bg-gray-50 transition-colors cursor-pointer group w-full text-left"
      >
        <Icon
          icon="mdi:lightbulb-outline"
          className={`h-3.5 w-3.5 flex-shrink-0 ${
            isStreaming ? 'text-amber-500 animate-pulse' : 'text-gray-400'
          }`}
        />
        <span className="text-xs text-gray-500 truncate">
          {summaryLabel}
          {toolCount > 1 && (
            <span className="text-gray-400 ml-1">&middot; {t('ai.thinkingTimeline.toolCount', { count: toolCount })}</span>
          )}
        </span>
        <Icon
          icon={isExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'}
          className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 ml-auto opacity-0 group-hover:opacity-100 transition-opacity"
        />
      </button>

      {/* Expanded timeline */}
      {isExpanded && (
        <div className="mt-1 ml-1 border-l-2 border-gray-200 pl-3 space-y-1.5">
          {steps.map((step, index) => (
            <ToolStep
              key={index}
              tool={step.tool}
              query={step.query}
              detail={step.detail}
              isActive={step.status === 'active'}
            />
          ))}
        </div>
      )}
    </div>
  );
}

ThinkingTimeline.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      tool: PropTypes.string.isRequired,
      query: PropTypes.string,
      status: PropTypes.oneOf(['active', 'completed']),
    }),
  ).isRequired,
  isStreaming: PropTypes.bool,
  isCollapsible: PropTypes.bool,
};

/**
 * A single tool step in the timeline.
 */
function ToolStep({ tool, query, detail, isActive }) {
  const { t } = useTranslation();
  const config = TOOL_CONFIG[tool] || DEFAULT_TOOL;

  return (
    <div className="flex items-start gap-1.5">
      {isActive ? (
        <div className="w-3.5 h-3.5 mt-0.5 border-2 border-amber-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
      ) : (
        <Icon
          icon="mdi:check-circle"
          className="h-3.5 w-3.5 mt-0.5 text-green-500 flex-shrink-0"
        />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <Icon icon={config.icon} className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
          <span className="text-xs text-gray-600 font-medium">{t(config.label)}</span>
        </div>
        {query && (
          <ExpandableStepText text={`"${query}"`} />
        )}
        {detail && (
          <ExpandableStepText text={detail} />
        )}
      </div>
    </div>
  );
}

ToolStep.propTypes = {
  tool: PropTypes.string.isRequired,
  query: PropTypes.string,
  detail: PropTypes.string,
  isActive: PropTypes.bool,
};

function ExpandableStepText({ text }) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef(null);

  useLayoutEffect(() => {
    if (isExpanded || !textRef.current) {
      setIsOverflowing(false);
      return undefined;
    }

    const updateOverflow = () => {
      const element = textRef.current;
      if (!element) return;
      setIsOverflowing(element.scrollWidth > element.clientWidth);
    };

    updateOverflow();
    const observer = new ResizeObserver(updateOverflow);
    observer.observe(textRef.current);
    return () => observer.disconnect();
  }, [isExpanded, text]);

  return (
    <div className="mt-0.5 min-w-0">
      <p
        ref={textRef}
        className={`text-xs text-gray-400 ${
          isExpanded ? 'whitespace-pre-wrap break-words' : 'truncate'
        }`}
      >
        {text}
      </p>
      {(isOverflowing || isExpanded) && (
        <button
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          className="mt-0.5 text-xs font-medium text-gray-500 hover:text-gray-700 cursor-pointer"
        >
          {isExpanded ? t('ai.thinkingTimeline.showLess') : t('ai.thinkingTimeline.showMore')}
        </button>
      )}
    </div>
  );
}

ExpandableStepText.propTypes = {
  text: PropTypes.string.isRequired,
};
